(() => {
  const esc = value => String(value ?? '').replace(/[&<>"']/g, ch => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[ch]));
  let botScript;
  let botLoadId = 0;
  function loadBot() {
    if (window.turnstile?.render) return Promise.resolve(window.turnstile);
    if (botScript) return botScript;
    botScript = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      const callbackName = 'lfdSetupTurnstileLoaded' + (++botLoadId);
      let settled = false;
      const finish = error => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        delete window[callbackName];
        if (error) { script.remove(); reject(error); }
        else resolve(window.turnstile);
      };
      const timer = setTimeout(() => finish(new Error('email_verification_unavailable')), 20000);
      window[callbackName] = () => finish(window.turnstile?.render ? null : new Error('email_verification_unavailable'));
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=' + callbackName;
      script.async = true;
      script.onerror = () => finish(new Error('email_verification_unavailable'));
      document.head.append(script);
    }).catch(error => { botScript = null; throw error; });
    return botScript;
  }
  class LFDSetupEmail {
    constructor(dialog) {
      this.dialog = dialog;
      this.active = true;
      this.setupId = '';
      this.storage = window.LFDSetupStorage;
      this.mode = this.storage?.emailMode();
      if (!this.storage?.enabled() || !['preview', 'test'].includes(this.mode)) return;
      this.requestId = crypto.randomUUID();
      this.bot = this.storage.emailBotProtection();
      this.botToken = '';
      this.botGeneration = 0;
      const inputId = 'lfd-email-' + this.requestId;
      this.form = document.createElement('form');
      this.form.className = 'sq-email-form';
      this.form.innerHTML = `<h3>${esc(this.text('title'))}</h3><label for="${inputId}">${esc(this.text('label'))}</label><div class="sq-email-controls"><input id="${inputId}" type="email" autocomplete="email" inputmode="email" maxlength="254" required placeholder="${esc(this.text('placeholder'))}"><button type="submit" class="sq-btn sq-btn-primary" disabled>${esc(this.text('send'))}</button></div><p class="sq-help">${esc(this.text('privacy'))}</p><p class="sq-help">${esc(this.text(this.mode === 'preview' ? 'preview' : 'test_note'))}</p><p class="sq-email-status" role="status" aria-live="polite"></p>`;
      const slot = dialog.querySelector('[data-saved-email-slot]');
      if (slot) slot.append(this.form);
      else dialog.insertBefore(this.form, dialog.querySelector('[data-action="saved-close"]'));
      this.input = this.form.querySelector('input');
      this.button = this.form.querySelector('button');
      this.status = this.form.querySelector('[role="status"]');
      if (this.bot.enabled && this.mode === 'test') {
        const panel = document.createElement('div');
        panel.className = 'sq-email-bot';
        panel.innerHTML = `<p class="sq-help">${esc(this.text('bot_privacy'))}</p><div data-email-bot></div><p class="sq-email-status" role="status" aria-live="polite"></p><button type="button" class="sq-btn" hidden>${esc(this.text('bot_retry'))}</button>`;
        this.form.insertBefore(panel, this.status);
        this.botMount = panel.querySelector('[data-email-bot]');
        this.botStatus = panel.querySelector('[role="status"]');
        this.botRetry = panel.querySelector('button');
        this.botRetry.addEventListener('click', () => this.startBot());
      }
      this.status.textContent = this.mode === 'preview' ? '' : this.text('waiting');
      this.form.addEventListener('submit', event => { event.preventDefault(); this.send(); });
    }
    text(key) { return this.storage.text('email_' + key); }
    setSetupId(id) {
      if (!this.form || !this.active || !this.storage.validId(id)) return;
      this.setupId = id;
      this.status.textContent = '';
      this.updateButton();
      if (this.botMount) this.startBot();
    }
    updateButton() {
      this.button.disabled = this.mode !== 'test' || !this.setupId || this.busy || this.sent || (this.bot.enabled && !this.botToken);
    }
    removeBot() {
      this.botGeneration++;
      this.botToken = '';
      if (this.widget !== undefined) {
        try { window.turnstile?.remove(this.widget); } catch {}
        this.widget = undefined;
      }
    }
    async startBot() {
      if (!this.active || !this.dialog.open || !this.botMount || this.sent || this.busy) return;
      this.removeBot();
      this.updateButton();
      const generation = this.botGeneration;
      const current = () => this.active && this.dialog.open && this.botGeneration === generation && !this.sent;
      const failed = () => {
        if (!current()) return;
        this.botToken = '';
        this.botStatus.textContent = this.text('bot_error');
        this.botRetry.hidden = false;
        this.updateButton();
      };
      this.botStatus.textContent = this.text('bot_waiting');
      this.botRetry.hidden = true;
      if (!/^[A-Za-z0-9_-]{20,200}$/.test(this.bot.sitekey) || /^[123]x0{10}/.test(this.bot.sitekey)) { failed(); return; }
      try {
        const api = await loadBot();
        if (!current()) return;
        this.widget = api.render(this.botMount, {
          sitekey: this.bot.sitekey, action: 'lfd_setup_email', cData: this.setupId + '_' + this.requestId,
          theme: 'auto', size: 'compact', appearance: 'interaction-only', retry: 'never',
          'refresh-expired': 'manual', 'response-field': false,
          callback: token => {
            if (!current()) return;
            if (typeof token !== 'string' || !token || token.length > 2048 || /\s/.test(token)) { failed(); return; }
            this.botToken = token;
            this.botStatus.textContent = '';
            this.botRetry.hidden = true;
            this.updateButton();
          },
          'expired-callback': failed, 'timeout-callback': failed, 'error-callback': () => { failed(); return true; }
        });
      } catch { failed(); }
    }
    destroy() {
      this.active = false;
      this.removeBot();
      if (this.input) this.input.value = '';
    }
    async send() {
      if (!this.active || !this.dialog.open || this.mode !== 'test' || !this.setupId || this.busy || this.sent) return;
      if (this.bot.enabled && !this.botToken) return;
      this.input.value = this.input.value.trim();
      if (!this.input.reportValidity()) return;
      this.busy = true;
      this.button.disabled = true;
      this.input.disabled = true;
      this.status.textContent = this.text('sending');
      try {
        await this.storage.email({id: this.setupId, email: this.input.value, requestId: this.requestId,
          ...(this.bot.enabled ? {botToken: this.botToken} : {})});
        if (!this.active || !this.dialog.open) return;
        this.sent = true;
        this.removeBot();
        if (this.botRetry) this.botRetry.hidden = true;
        this.input.value = '';
        this.status.textContent = this.text('sent');
      } catch (error) {
        if (!this.active || !this.dialog.open) return;
        const messages = {email_disabled: 'disabled', email_test_only: 'test_only', email_limited: 'limited', email_uncertain: 'uncertain', email_verification: 'bot_error', email_verification_unavailable: 'bot_error'};
        this.status.textContent = this.text(messages[error.message] || 'error');
      } finally {
        this.busy = false;
        if (this.active && this.dialog.open) {
          this.input.disabled = !!this.sent;
          this.updateButton();
          if (this.botMount && !this.sent) this.startBot();
        }
      }
    }
  }
  window.LFDSetupEmail = LFDSetupEmail;
})();

